'use client';

import { useEffect, useState } from 'react';
import { setCookie, getCookie } from 'cookies-next';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { Button, Anchor } from '@components/common';
import sendGoogleTagEvent from '@hooks/useSendGoogleTagEvent';

const assetsPrefix = process.env.ASSETS_PREFIX_FRONTEND || '';
const cdnUrl = process.env.NODE_ENV === 'production' ? assetsPrefix : '';

// Constants
const COOKIE_NAME = 'smart-app-download';
const COOKIE_EXPIRATION_DAYS = 15;

const DEVICE_CONFIG = {
  android: {
    appName: 'BestJobs',
    appLink: 'https://play.google.com/store/apps/details?id=ro.bestjobs.androidapp',
    storeName: 'In Google Play',
    icon: 'android-chrome-192x192.png',
  },
  ios: {
    appName: 'BestJobs',
    appLink: 'https://itunes.apple.com/ro/app/bestjobs/id584324978',
    storeName: 'On the App Store',
    icon: 'apple-touch-icon.png',
  },
} as const;

/**
 * @description SmartAppBanner component for displaying cross-platform app download banner
 *
 * It detects mobile devices to show appropriate app download links on all pages.
 */
export const SmartAppBanner = () => {
  const [isSSR, setIsSSR] = useState(true);
  const [isDismissed, setIsDismissed] = useState(false);
  const [device, setDevice] = useState<'android' | 'ios' | null>(null);

  // Detect if user is on Safari iOS (to hide banner since native one appears)
  const isSafariIOS = (): boolean => {
    if (typeof window === 'undefined') return false;
    const { userAgent } = window.navigator;
    const isIOS = /iPhone|iPad|iPod/i.test(userAgent);
    // Exclude other iOS browsers: CriOS (Chrome), FxiOS (Firefox), EdgiOS (Edge), OPiOS (Opera)
    // These browsers include "Safari" in their UA string for compatibility, so we need to filter them out
    const isSafari = /Safari/i.test(userAgent) && !/CriOS|FxiOS|EdgiOS|OPiOS/i.test(userAgent);
    return isIOS && isSafari;
  };

  // Detect device type (returns null if not a supported mobile device)
  const detectDevice = (): 'android' | 'ios' | null => {
    if (typeof window === 'undefined') return null;
    const { userAgent } = window.navigator;
    if (/Android/i.test(userAgent)) {
      return 'android';
    }
    if (/iPhone|iPad|iPod/i.test(userAgent)) {
      return 'ios';
    }
    return null;
  };

  // Handle SSR and initialization
  useEffect(() => {
    setIsSSR(false);

    // Hide banner on Safari iOS (native smart banner appears there)
    if (isSafariIOS()) {
      setIsDismissed(true);
      return;
    }

    // Check if banner was dismissed via cookie
    const dismissedCookie = getCookie(COOKIE_NAME);
    if (dismissedCookie !== undefined) {
      setIsDismissed(true);
      return;
    }

    // Detect device (returns null if not mobile)
    const detectedDevice = detectDevice();

    if (detectedDevice) {
      setDevice(detectedDevice);
    } else {
      setIsDismissed(true);
    }
  }, []);

  const hideBanner = () => {
    setIsDismissed(true);
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + COOKIE_EXPIRATION_DAYS);
    void setCookie(COOKIE_NAME, 'false', {
      expires: expirationDate,
      path: '/',
    });
  };

  // Handle banner dismissal
  const handleDismiss = () => {
    sendGoogleTagEvent('smart_banner_close', 'Custom');
    hideBanner();
  };

  // VIEW counts as a considered decision at the store listing — hide for the same
  // period as a dismissal instead of re-prompting a user who already engaged.
  const handleView = () => {
    sendGoogleTagEvent('smart_banner_open', 'Custom');
    hideBanner();
  };

  // Don't render during SSR, if dismissed, or if no device detected
  if (isSSR || isDismissed || !device) {
    return null;
  }

  const config = DEVICE_CONFIG[device];
  const appIcon = `${cdnUrl}/favicon/${config.icon}`;

  return (
    <div className="relative z-[11] w-full bg-gray-100">
      <div className="flex items-center gap-2 p-2">
        {/* Close button */}
        <Button
          styling="none"
          onClick={handleDismiss}
          className="ml-1 mt-1 shrink-0 hover:opacity-80"
          aria-label="Close banner"
        >
          <XMarkIcon className="size-5 text-primary" />
        </Button>

        {/* App icon */}
        <div className="shrink-0 py-2 text-center">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={appIcon}
            alt={config.appName}
            className="mx-auto max-w-[44px]"
          />
        </div>

        {/* App info and button */}
        <div className="flex flex-1 items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-gray-900">{config.appName}</div>
            <div className="truncate text-xs text-gray-600">{config.storeName}</div>
          </div>
          <Anchor
            href={config.appLink}
            styling="solid"
            className="shrink-0 !text-white"
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleView}
          >
            VIEW
          </Anchor>
        </div>
      </div>
    </div>
  );
};
